import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  IconButton,
  Badge,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  NotificationsNone,
  Close,
  Create,
  CheckCircle,
  Warning,
  Error,
  Info,
  Schedule,
  Person,
  Visibility,
  MarkEmailRead
} from '@mui/icons-material';
import { Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';

const DesignCaseNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  useEffect(() => {
    fetchNotifications();
    // Polling for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      // TODO: Replace with actual API call
      const mockNotifications = [
        {
          id: 1,
          type: 'case_created',
          title: 'Design case mới được tạo',
          message: 'Case ORD-001 đã được tạo thành công',
          timestamp: new Date(Date.now() - 1000 * 60 * 5), // 5 minutes ago
          read: false,
          caseId: 1,
          orderId: 'ORD-001'
        },
        {
          id: 2,
          type: 'design_uploaded',
          title: 'Design đã được upload',
          message: 'Designer Sarah đã upload design cho case ORD-002',
          timestamp: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
          read: false,
          caseId: 2,
          orderId: 'ORD-002'
        },
        {
          id: 3,
          type: 'case_assigned',
          title: 'Case đã được assign',
          message: 'Case ORD-003 đã được assign cho Designer Mike',
          timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
          read: true,
          caseId: 3,
          orderId: 'ORD-003'
        },
        {
          id: 4,
          type: 'deadline_approaching',
          title: 'Deadline sắp đến',
          message: 'Case ORD-004 có deadline trong 2 giờ tới',
          timestamp: new Date(Date.now() - 1000 * 60 * 60), // 1 hour ago
          read: false,
          caseId: 4,
          orderId: 'ORD-004'
        }
      ];

      setNotifications(mockNotifications);
      setUnreadCount(mockNotifications.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      // TODO: Implement API call
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // TODO: Implement API call
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
      toast.success('Đã đánh dấu tất cả thông báo đã đọc');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setShowDetailDialog(true);
    if (!notification.read) {
      markAsRead(notification.id);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
          case 'case_created':
      return <Create color="primary" />;
      case 'design_uploaded':
        return <CheckCircle color="success" />;
      case 'case_assigned':
        return <Person color="info" />;
      case 'deadline_approaching':
        return <Warning color="warning" />;
      case 'case_completed':
        return <CheckCircle color="success" />;
      case 'case_rejected':
        return <Error color="error" />;
      default:
        return <Info color="primary" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'case_created':
        return 'primary';
      case 'design_uploaded':
        return 'success';
      case 'case_assigned':
        return 'info';
      case 'deadline_approaching':
        return 'warning';
      case 'case_completed':
        return 'success';
      case 'case_rejected':
        return 'error';
      default:
        return 'primary';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Vừa xong';
    if (minutes < 60) return `${minutes} phút trước`;
    if (hours < 24) return `${hours} giờ trước`;
    return `${days} ngày trước`;
  };

  const getNotificationTitle = (type) => {
    switch (type) {
      case 'case_created':
        return 'Case mới';
      case 'design_uploaded':
        return 'Design uploaded';
      case 'case_assigned':
        return 'Case assigned';
      case 'deadline_approaching':
        return 'Deadline sắp đến';
      case 'case_completed':
        return 'Case hoàn thành';
      case 'case_rejected':
        return 'Case bị từ chối';
      default:
        return 'Thông báo';
    }
  };

  return (
    <>
      {/* Notification Button for Sidebar */}
      <button
        onClick={() => setDrawerOpen(true)}
        className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors w-full relative"
      >
        <div className="relative">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </div>
        <span>Thông báo</span>
      </button>

      {/* Notifications Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '90vw'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Thông Báo
            </Typography>
            <Box>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<MarkEmailRead />}
                  onClick={markAllAsRead}
                  sx={{ mr: 1 }}
                >
                  Đánh dấu đã đọc
                </Button>
              )}
              <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <List sx={{ p: 0 }}>
            <AnimatePresence>
              {notifications.map((notification, index) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      backgroundColor: notification.read ? 'transparent' : 'action.hover',
                      '&:hover': {
                        backgroundColor: 'action.selected'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {notification.title}
                          </Typography>
                          {!notification.read && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'primary.main'
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {notification.message}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatTimeAgo(notification.timestamp)}
                            </Typography>
                            <Chip
                              label={getNotificationTitle(notification.type)}
                              size="small"
                              color={getNotificationColor(notification.type)}
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                </motion.div>
              ))}
            </AnimatePresence>
          </List>

          {notifications.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsNone sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Không có thông báo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bạn sẽ nhận được thông báo khi có cập nhật về design cases
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Notification Detail Dialog */}
      <Dialog
        open={showDetailDialog}
        onClose={() => setShowDetailDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedNotification && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'transparent' }}>
                  {getNotificationIcon(selectedNotification.type)}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {selectedNotification.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatTimeAgo(selectedNotification.timestamp)}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" paragraph>
                {selectedNotification.message}
              </Typography>
              
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Chi tiết:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Order ID: {selectedNotification.orderId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Case ID: {selectedNotification.caseId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Loại: {getNotificationTitle(selectedNotification.type)}
                </Typography>
              </Paper>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Bạn có thể xem chi tiết case trong trang quản lý design cases
                </Typography>
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDetailDialog(false)}>
                Đóng
              </Button>
              <Button
                variant="contained"
                startIcon={<Visibility />}
                onClick={() => {
                  setShowDetailDialog(false);
                  // TODO: Navigate to case detail
                  toast.success('Chuyển đến trang chi tiết case');
                }}
              >
                Xem chi tiết
              </Button>
            </DialogActions>
          </motion.div>
        )}
      </Dialog>

      {/* Real-time notification toast */}
      <Snackbar
        open={false} // TODO: Implement real-time notifications
        autoHideDuration={6000}
        onClose={() => {}}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => {}}
          severity="info"
          sx={{ width: '100%' }}
        >
          <Typography variant="body2">
            Có thông báo mới về design case
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default DesignCaseNotifications; 
